/* eslint-disable no-restricted-properties */
/* eslint-disable @typescript-eslint/ban-ts-comment */

import jwtDecode from 'jwt-decode';
import get from 'lodash/get';
import { createSelector } from 'reselect';
import moment from 'moment/moment';
import { toast, ToastContent, ToastOptions } from 'react-toastify';
import startCase from 'lodash/startCase';
import mapValues from 'lodash/mapValues';
import isObject from 'lodash/isObject';
import isArray from 'lodash/isArray';
import lowerCase from 'lodash/lowerCase';
import isString from 'lodash/isString';
import { useForm } from 'react-final-form';
import intersection from 'lodash/intersection';
import transform from 'lodash/transform';
import isEqual from 'lodash/isEqual';
import without from 'lodash/without';
import camelCase from 'lodash/camelCase';
import uniq from 'lodash/uniq';

import { localStorageService } from 'services/localStorage';

// eslint-disable-next-line import/no-cycle
import { getParsedUserByAppType, updateFirstName } from 'shared/functions/user';
// eslint-disable-next-line import/no-cycle
import { upperFirst } from 'shared/functions/strings';
import { momentify } from 'shared/functions/date';
import { keyExists } from 'shared/functions/objects';
import { Archivable } from '@eva/types/dto';

import { requestBackend } from 'utils/request/request';
import { isImmutable, type ListedResponse, type EntityActionName, type MaybeImmutable } from 'types/common-types';
import { sentry } from 'utils/tracing';
import { OrigContextType } from 'utils/tracing/types';

import type { AccountType } from 'types/generic-context';

import {
  accountTypeAccessPortal,
  actionsInContinous,
  actionsInInfinitive,
  actionsInPastSimple,
  agentContactAccountTypes,
  appTypes,
  defaultBulkSelection,
  employmentTypes,
  entitiesTypes,
  entityTypes,
  hostnames,
  jobsStatuses,
  locationKeys,
  minPasswordLength,
  removeScriptRegex,
  subscriptionTypes,
  workLocationPreferenceTypes,
  zeroTime,
} from '../../constants';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const packageJson = require('../../../../package.json');

export const getPackageVersion = () => {
  try {
    return [...packageJson.version.split('.').slice(0, -1), '0'].join('.');
  } catch (err) {
    logError('can\t get package version');
  }
};

export const upsert = <T>(list: T[], targetItem: T, idKey: string | ((item: T) => number)) => {
  const targetId = typeof idKey === 'function' ? idKey(targetItem) : targetItem[idKey];

  const index = list.findIndex((item) => {
    const id = typeof idKey === 'function' ? idKey(item) : item[idKey];
    return id === targetId;
  });

  if (index < 0) {
    return [...list, targetItem];
  }

  const updatedList = [...list];

  updatedList[index] = {
    ...list[index],
    ...targetItem,
  };

  return updatedList;
};

export const upsertList = <T>(list: T[], itemsToUpsert: T[], idKey: string) =>
  itemsToUpsert ? itemsToUpsert.reduce((acc, item) => upsert(acc, item, idKey), list) : list;

export const upsertPagedData = <E>(currentData: ListedResponse<E>, updatedItem: E, entityIdKey: string) => {
  const items = upsert(currentData.items, updatedItem, entityIdKey);

  return {
    ...currentData,
    items,
    total: items.length,
  };
};

export const upsertPagedDataList = <E>(currentData: ListedResponse<E>, updatedItems: E[], entityIdKey: string) => {
  const items = upsertList(currentData.items, updatedItems, entityIdKey);

  return {
    ...currentData,
    items,
    total: items.length,
  };
};

export const deleteFromPagedData = <E>(currentData: ListedResponse<E>, updatedItem: E, entityIdKey: string) => {
  const nextItems = currentData.items.filter((item) => item[entityIdKey] !== updatedItem[entityIdKey]);

  return {
    ...currentData,
    items: nextItems,
    total: currentData.total - 1,
  };
};

export const setListedResponseCache = <E>(queryClient: any, cacheName: string, nextData: ListedResponse<E>) => {
  queryClient.setQueryData(cacheName, nextData);
};

export const upsertToListedResponseCache = <E>(
  queryClient: any,
  cacheName: string,
  entityIdKey: string,
  listedResponse: ListedResponse<E>,
  updatedEntry: Partial<E>,
) => {
  const nextData = upsertPagedData(listedResponse, updatedEntry, entityIdKey);

  queryClient.setQueryData(cacheName, nextData);
};

export const upsertListToListedResponseCache = <E>(
  queryClient: any,
  cacheName: string,
  entityIdKey: string,
  listedResponse: ListedResponse<E>,
  updatedEntries: Partial<E>[],
) => {
  const nextData = upsertPagedDataList(listedResponse, updatedEntries, entityIdKey);

  queryClient.setQueryData(cacheName, nextData);
};

export const deleteFromListedResponseCache = <E>(
  queryClient: any,
  cacheName: string,
  entityIdKey: string,
  listedResponse: ListedResponse<E>,
  deletedEntry: E,
) => {
  const nextData = deleteFromPagedData(listedResponse, deletedEntry, entityIdKey);

  queryClient.setQueryData(cacheName, nextData);
};

export const getPagedDataReceivedLength = <T>(receivedPages: ListedResponse<T>[]) =>
  receivedPages.reduce((acc, page) => acc + page.items?.length ?? 0, 0) ?? 0;

export const getPagedDataTotal = <T>(receivedPages: ListedResponse<T>[]) => receivedPages?.[0]?.total ?? 0;

export const getPagedDataFirstItem = <T>(receivedPages: ListedResponse<T>[]) => receivedPages?.[0]?.items?.[0];

export const findInPagedData = <T>(receivedPages: ListedResponse<T>[], cb: (item: T) => boolean) => {
  const all = receivedPages.flatMap((page) => page.items);

  return all.find(cb);
};

export const getPlainValues = <T>(o: MaybeImmutable<T>): T => (isImmutable(o) ? o.toJS() : (o as T));

export const pluralize = (count: number, noun: string, suffix = 's') => `${noun}${count !== 1 ? suffix : ''}`;

export const registerAnalyticsSession = (appType: ValuesOf<typeof appTypes>) => {
  if (typeof window.regAnalyticsSession !== 'function') {
    return;
  }
  const storageUser = localStorage.user ? getParsedUserByAppType(appType) : {};
  const storageUserGroupIds = localStorage.userGroupIds;
  const storageUserDisplayName = localStorage.userDisplayName;

  if (!storageUser?.userId == null) {
    return;
  }

  window.regAnalyticsSession(storageUser.userId, storageUser.accountTypes, storageUserDisplayName, storageUserGroupIds);
};

export const isEntityArchived = <T extends Archivable>(entity: T) =>
  !!entity?.archive?.archivedAt || entity?.archive?.isArchived;

export const isEntityNotArchived = <T extends Archivable>(entity: T) => !isEntityArchived(entity);

export const getActionInInfinitive = (actionName: EntityActionName) => actionsInInfinitive[actionName] || actionName;

export const getActionInPresentContinous = (actionName: EntityActionName) =>
  actionsInContinous[actionName] || 'processing';

export const getActionInPastSimple = (actionName: EntityActionName) => actionsInPastSimple[actionName] || 'processed';

export const getTokenPayload = (token: string) => jwtDecode(token);

// Used in mapStateToProps
export const prepareSelector = (reducer: string, path: string) =>
  createSelector(
    // @ts-expect-error
    (state) => state && state.get(reducer),
    (state) => state && state.get(path),
  );

export const adaptIsTerminal = (isTerminal: boolean) => (isTerminal ? translate('(Negative)') : '');

export const validatePassword = (password: string) => {
  if ((password || '').length < minPasswordLength) {
    return translate('Passwords must be at least {{minPasswordLength}} characters in length', {
      minPasswordLength,
    });
  }
};

export const openTags = (messageContent: any) => {
  let strippedContent = messageContent;
  while (strippedContent.includes('< ')) {
    strippedContent = strippedContent.replace('< ', '');
  }
  const splittedContent = strippedContent.split('/>');

  return splittedContent.reduce((prev: any, cur: any, index: any) => {
    if (index === splittedContent.length - 1) {
      return `${prev}${cur}`;
    }
    const startIndex = cur.lastIndexOf('<');
    const cuttedTag = cur.substr(startIndex + 1);
    const endIndex = cuttedTag.indexOf(' ');
    const tag = cur.substr(startIndex + 1, endIndex).trim();
    return `${prev}${cur.substr(0, startIndex)}${cur.substr(startIndex).trim()}></${tag}>`;
  }, '');
};

export const onlineTooltip = (chat: any, timeFormat = 'DD/MM/YY hh:mm') => {
  const momentifiedSeenOnline =
    // @ts-expect-error
    chat.lastSeenOnline && momentify(chat.lastSeenOnline).format(timeFormat);
  if (chat.isOnline) {
    return `Online from ${momentifiedSeenOnline}`;
  } else if (chat.lastSeenOnline) {
    return `Offline from ${momentifiedSeenOnline}`;
  }
  return 'Never been online';
};

export const collectSelections = (json: any, id: any) => {
  try {
    const selections = json._embedded.custom_fields.find((item: any) => item.id === id);
    if (selections) {
      const options = selections._embedded.definition.field.selections;
      if (Array.isArray(selections.value)) {
        return selections.value.map((valueId: any) => options.find((option: any) => option.id === valueId));
      }
      return options.find((option: any) => option.id === selections.value);
    }
    return '?';
  } catch (err) {
    logError(`Collect selection ${id} parse error for ${json.id}`);
    logError(err);
    return [];
  }
};

export const collectLocation = (address: any) =>
  ['city', 'state', 'postal_code']
    .reduce((prev, cur) => (address[cur] ? [...prev, address[cur]] : prev), [])
    .join(', ');

export const stringsToObjects = (array: any) => array.map((item: any) => ({ id: item, label: item }));

export const stringifyActionError = (action: any) => {
  const { payload = {} } = action.error;
  if (payload.error) {
    return payload.error;
  } else if (payload.errors) {
    return payload.errors
      .map((error: any) => {
        const joinedPath = Array.isArray(error.path) ? error.path.join('.') : '';
        return `${joinedPath ? `${joinedPath}: ` : ''}: ${error.message}`;
      })
      .join(', ');
  } else if (action.error) {
    if (typeof action.error === 'string') {
      return action.error;
    } else if (typeof action.error === 'object') {
      const { url, message } = action.error;
      return `${message} ${url}`;
    }
  }
  return 'Unknown server error';
};

export const kilofy = (value: string | number) => (value || '').toString().replace(/000(?=\D|$)/g, 'k');

export function defineJobsFilters() {
  this.filters = [
    {
      label: translate('ALL'),
      statuses: { ids: [] },
    },
    {
      label: translate('ACTIVE'),
      statuses: jobsStatuses.active,
    },
    {
      label: translate('ON HOLD'),
      statuses: jobsStatuses.onHold,
    },
    {
      label: translate('CLOSED'),
      statuses: jobsStatuses.closed,
    },
  ];
}

export function updateBulkSelection() {
  const { bulkSelected } = this.state;
  const candidates = this.state.candidates || this.props.candidates;
  Object.keys(bulkSelected).forEach((compositeId) => {
    if (!candidates.find((candidate: any) => candidate.compositeId === compositeId)) {
      delete bulkSelected[compositeId];
    }
  });
  const bulkPaneCandidates = candidates.filter((item: any) => bulkSelected[item.compositeId]);
  this.setState({
    bulkSelected,
    bulkPaneCandidates,
    showBulkPane: Object.values(bulkSelected).filter(Boolean).length,
    ...(bulkPaneCandidates.length ? {} : defaultBulkSelection),
  });
}

export function processBulkSelection(selectedCandidate: any, evt: any) {
  if (typeof evt !== 'object') {
    return true;
  }

  const candidates = this.props.candidates || this.state.candidates;
  const { bulkSelected } = this.state;
  const isCheckedEvent = typeof evt.checked === 'boolean';

  if (isCheckedEvent) {
    // let selectCandidate = false;
    // 1. Если кликнули на чекбоксе:
    // 1.1. Если кандидат расчекбокснут - убираем его чексбокность и возвращаем false
    let updatedBulkSelected = copy(bulkSelected);
    // 1.2. Если кандидат чекбокснут - то:
    // 1.2.1. Если shift - делаем выделение от выбранного до текущего
    // Ctrl теперь ни на что не влияет - просто добавляем текущего в выделение
    // 1.2.2. Если балк пустой - меняем selectedCandidate на текущего
    if (!evt.shiftKey) {
      this.setState({
        lastCheckedCandidate: selectedCandidate,
        lastCandidateWasChecked: evt.checked,
      });
      if (evt.checked) {
        updatedBulkSelected = {
          ...bulkSelected,
          [selectedCandidate.compositeId]: true,
        };
      } else {
        delete updatedBulkSelected[selectedCandidate.compositeId];
      }
    } else if (this.state.lastCheckedCandidate) {
      const indexOfSelected = candidates.indexOf(this.state.lastCheckedCandidate);
      const indexOfCurrent = candidates.indexOf(selectedCandidate);
      updatedBulkSelected = candidates
        .slice(Math.min(indexOfSelected, indexOfCurrent), Math.max(indexOfSelected, indexOfCurrent) + 1)
        .reduce(
          (prev, cur) => ({
            ...prev,
            [cur.compositeId]: this.state.lastCandidateWasChecked,
          }),
          bulkSelected,
        );
      document.getSelection().removeAllRanges();
    }

    this.setState(
      {
        bulkSelected: updatedBulkSelected,
      },
      updateBulkSelection.bind(this),
    );

    return false; // select candidate or not
  }

  // Если кликнули не на чекбоксе:
  // Если кандидат чексбокснутый - просто возвращаем true и не меняем балк
  // Если не чексбокнутый - сбрасываем балк, возвращаем true - ОТМЕНЕНО!
  // if (!bulkSelected[selectedCandidate.compositeId]) {
  //   this.setState(defaultBulkSelection);
  // }
  return true;
}

export const mapJobInReducer = (job) => ({
  ...job,
  location: job.locations[0],
});

export const overnightAvailability = (originalDays: Array<any>) => {
  const days = copy(originalDays);
  days.forEach((day, index) => {
    const daySlots = day.slots;
    const nextDaySlots = days[index === 6 ? 0 : index + 1].slots;
    const nextStartsOvernightIndex = nextDaySlots.findIndex((item) => item.startTime === zeroTime);
    const currentEndsOvernightIndex = daySlots.findIndex((item) => item.endTime === zeroTime);
    if (currentEndsOvernightIndex > -1) {
      if (nextStartsOvernightIndex > -1) {
        daySlots[currentEndsOvernightIndex].endTime = nextDaySlots[nextStartsOvernightIndex].endTime;
        nextDaySlots.splice(nextStartsOvernightIndex, 1);
      }
    }
  });
  return days;
};

export const preparePermanentAvailability = (prePermanentAvailability) => {
  const permanentAvailability = prePermanentAvailability || {
    description: '',
    holidayAllowance: false,
    days: [],
  };
  const weekdaysCodes = moment.weekdays();
  weekdaysCodes.push(weekdaysCodes.shift());
  return {
    ...permanentAvailability,
    description: permanentAvailability.description || '',
    holidayAllowance: permanentAvailability.holidayAllowance || false,
    days: weekdaysCodes.reduce(
      (prev, cur) => [
        ...prev,
        permanentAvailability.days.find((item) => item.weekDay === cur) || {
          weekDay: cur,
          slots: [],
          flexibleHours: false,
        },
      ],
      [],
    ),
  };
};
export const prepareInterimAvailability = (preInterimAvailability) =>
  preInterimAvailability || {
    description: '',
    slots: [],
  };

export const padTwo = (number: number | string) => number.toString().padStart(2, '0');

export const whiteListObject = (whiteListKeys: string[]) => (object: Record<string, any>) =>
  object &&
  typeof object === 'object' &&
  Object.keys(object).forEach((key) => !whiteListKeys.includes(key) && delete object[key]);

export const whitelistLocation = whiteListObject(locationKeys);
const wlSalaryKeys = ['period', 'value'];
export const whitelistSalary = whiteListObject(wlSalaryKeys);
const wlPaymentKeys = ['payValueMax', 'payValueMin', 'period'];
export const whitelistPayment = whiteListObject(wlPaymentKeys);
const wlNameKeys = ['firstName', 'lastName', 'middleName', 'title', 'note'];
export const whitelistName = (nameSection) => {
  if (!nameSection || typeof nameSection !== 'object') {
    return;
  }
  wlNameKeys.forEach((wlNameKey) => {
    if (nameSection[wlNameKey] === '') {
      nameSection[wlNameKey] = null;
    }
  });
  return whiteListObject(wlNameKeys)(nameSection);
};

export const typeaheadResultsPlaceholder = (isLoading: boolean, isEmpty: boolean) => {
  if (isLoading) {
    return `${translate('Loading')}...`;
  } else if (isEmpty) {
    return translate('Start typing...');
  }
  return upperFirst(translate('not found'));
};

export const popoverEventCanceler = (evt: Event) => {
  evt.stopPropagation();
  return false;
};

export const setNotificationComponent = (body: ToastContent, _flag: never, options: ToastOptions = {}) =>
  toast(body, options);

export const onlyKeys = (items: Array<any>, key: string) =>
  items.reduce((prev, cur) => [...prev, { [key]: cur[key] }], []);

export const displayNameFilterOptions = (options: any[], searchString?: string, selectedOptions?: any[]) =>
  options
    .filter((item) => (Array.isArray(selectedOptions) ? !selectedOptions.includes(item) : selectedOptions !== item))
    .filter((item) => (item.name.displayName || '').toLowerCase().includes(searchString.toLowerCase()));

export const copyToClipboard = (target: Node) => {
  const selection = window.getSelection();
  const range = document.createRange();
  range.selectNodeContents(target);
  selection.removeAllRanges();
  selection.addRange(range);
  const selectionText = selection.toString();
  if (!selectionText) {
    // eslint-disable-next-line no-alert
    return alert('Selection to copy was not found!');
  }
  try {
    document.execCommand('copy');
  } catch (err) {
    // eslint-disable-next-line no-alert
    alert('Oops, unable to copy');
  }
  return selectionText;
};

export const replaceTags = (text: string, tags) => {
  if (!text) {
    throw new Error('Text should be defined');
  }
  return tags.reduce((prev, cur) => {
    if (!prev) {
      logError(tags);
      throw new Error('Incorrect tags format');
    }
    return prev.replace(new RegExp(cur.code, 'g'), cur.name);
  }, text);
};

export const salaryRateLabel = (employmentType: ValuesOf<typeof employmentTypes>) => {
  if (!employmentType) {
    return translate('salary/rate');
  } else if (employmentType === employmentTypes.interimTemporary) {
    return translate('rate');
  }
  return translate('salary');
};

export const minLength = (min: number) => (value: string) =>
  value && value.length < min ? translate('Must be {{min}} characters or more', { min }) : undefined;
export const getConversationId = (payload) => {
  const { message = {} } = payload;
  const mergedPayload = Object.assign({}, payload, message);
  return mergedPayload.conversationId || mergedPayload.webchatId || mergedPayload.omnichatId;
};

export const getMessageSender = (message) => {
  if (!message) {
    return {};
  }
  const messageTypeCode = get(message, 'messageType.code', 'text');
  return (
    (message[messageTypeCode] || message.text || message).sender || {
      userId: 8,
      name: {
        displayName: 'Eva System',
      },
    }
  );
};
export const mapTags = (tags) =>
  tags.map((tag) => ({
    ...tag,
    name: `{{ ${tag.name} }}`,
    code: `{{ ${tag.code} }}`,
  }));
export const consistentBranding = (brand) => (startCase(brand) === 'cvLibrary' ? 'CV-Library' : '');
export const encodeRedirect = (location) => encodeURIComponent(`${location.pathname}${location.search}`);
export const stripToId = (values, key, passedIdKey, passedValueIdKey) => {
  const defaultKey = `${key}Id`;
  const idKey = passedIdKey || defaultKey;
  const valueIdKey = passedValueIdKey || defaultKey;
  const passedValue = values[key];
  const idExtractable = passedValue && typeof passedValue === 'object';
  values[valueIdKey] = idExtractable ? passedValue[idKey] : passedValue;
  delete values[key];
};
export const getValidateRequired = (allowedValue = Symbol.for('allowedValue')) => {
  const required = (initialValue) => {
    const value = toJsIfImmutable(initialValue);
    let valueFilled;
    if (Array.isArray(value)) {
      valueFilled = value.length;
    } else {
      valueFilled = value || value === allowedValue;
    }
    return valueFilled ? undefined : translate('Field is required');
  };
  return [required];
};

export const validateRichHTML = (tags: { code: string }[]) => (html: string) => {
  const div = document.createElement('div');
  div.innerHTML = html;
  const matches = div.innerText.match(/{{(.*?)}}/g) || [];
  const invalidTag = matches.find((match) => {
    const strippedMatch = match.replace('{{', '').replace('}}', '').trim();
    const foundTag = tags.find((tag) => tag.code === `{{ ${strippedMatch} }}`);
    if (!foundTag) {
      return strippedMatch;
    }
  });
  if (invalidTag) {
    return translate('Tag {{ invalidTag }} not found', {
      invalidTag,
    });
  }
  const openMatches = div.innerText.match(new RegExp('{{', 'g')) || [];
  const closeMatches = div.innerText.match(new RegExp('}}', 'g')) || [];
  if (openMatches.length !== closeMatches.length) {
    return translate('Open tags {{ count is not equal with close tags }} count');
  }
};

export const populateLocation = (change: any, location: string, prefix = 'location.') =>
  locationKeys.forEach((fieldKey) => change(`${prefix}${fieldKey}`, get(location, fieldKey, '')));
export const populateLocationFromCompanyId = async (companyId: number, locationToFill: Record<any, any>) => {
  const change = (field, value) => (locationToFill[field] = value);
  return requestBackend(`/companies/${companyId}`).then(({ location }) => populateLocation(change, location, ''));
};
export const showStickyDate = (show = true) => {
  const stickyDateElement: HTMLElement = document.querySelector('#messages-sticky-date');
  if (!stickyDateElement) {
    return logError('stickyDateElement not found!');
  } else if (show === false) {
    stickyDateElement.style.display = 'none';
  }
  const messageWrapperElements = Array.prototype.slice.call(document.querySelectorAll('.message-wrapper'));
  const bodyRect = document.body.getBoundingClientRect();
  const paneRect = stickyDateElement.getBoundingClientRect();
  const overlappedMessage = messageWrapperElements.reverse().find((element) => {
    const elemRect = element.getBoundingClientRect();
    return elemRect.top - bodyRect.top < paneRect.bottom;
  });
  if (overlappedMessage) {
    stickyDateElement.innerText = overlappedMessage.getAttribute('data-date');
    stickyDateElement.style.display = 'block';
  } else {
    stickyDateElement.style.display = 'none';
  }
};

export const invalidBounce = (emails, emailType) => get(emails, `${emailType}.bounceTypeId`) === 1;

export const isExternalCandidate = (compositeId: string) =>
  compositeId && `${parseInt(compositeId, 0)}` !== `${compositeId}`;

export const normalizeValue = <T>(value: T) => {
  if (Array.isArray(value)) {
    return value.map(normalizeValue);
  }

  if (typeof value === 'object') {
    return mapValues(value, normalizeValue);
  }

  if (typeof value === 'string' && ['true', 'false'].includes(value)) {
    return value === 'false' ? false : true;
  }

  return value;
};

export const iterateByArray = (values, fn) => {
  Object.keys(values).forEach(function (key) {
    const value = values[key];

    if (value !== null && isObject(value) && !isArray(value)) {
      iterateByArray(values[key], fn);
      return;
    }

    const newValue = fn(key, value);
    if (newValue) {
      values[key] = newValue;
    }
  });
};

export const mapFilterDataToNumber = (key, value) => {
  const keysToNumberIdForFilter = ['Ids', 'Id'];

  const isValueToNumber = keysToNumberIdForFilter.some((item) => key.includes(item));

  if (!isValueToNumber) {
    return;
  }

  return (isString(value) ? value.split(',') : value).map((item) => Number(item));
};

export const parseArrays = (items) => {
  const values = copy(items);
  iterateByArray(values, (key, value) => {
    if (values.hasOwnProperty('filter') && keyExists(values.filter, key) && isArray(value)) {
      return mapFilterDataToNumber(key, value);
    } else if (lowerCase(key).includes('ids') && isString(value)) {
      return value.split(',').map((str) => Number(str));
    }
  });
  return values;
};

export const getOpenedInModal = (props) => props.parent.constructor.name === 'ModalEntity';

export const connectInfluencingEvents = (self) => {
  const { socket } = self.context;
  const { influencingEvents } = self.props;

  if (getOpenedInModal(self.props)) {
    return;
  }

  if (influencingEvents) {
    if (!socket) {
      logError(self);
      logError('Socket should be present to connect influencing events');
      return;
    }
    self.eventHandlers = {};
    Object.entries(influencingEvents).forEach(([eventKey, eventHandler]) => {
      // @ts-expect-error
      self.eventHandlers[eventKey] = (...args) => eventHandler(self, ...args);
      socket.on(eventKey, self.eventHandlers[eventKey]);
    });
  }
};

export const disconnectInfluencingEvents = (self) => {
  const { socket } = self.context;
  if (socket && self.eventHandlers) {
    Object.entries(self.eventHandlers).forEach(([eventKey, eventHandler]) =>
      socket.removeListener(eventKey, eventHandler),
    );
  }
};

export const getBusinessWorkflowStateLabel = (option) => `${option.name} ${adaptIsTerminal(option.isTerminal)}
 ${option.disabledFrom ? `(${translate('Disabled')})` : ''}`;

export const getWorkflowStateOptionLabel = (showStage, showWorkflow) => (initialOption) => {
  const { normalizedPipelineWorkflows, normalizedPipelineStages } = window.settings;

  const option = initialOption || {};
  const workflow = normalizedPipelineWorkflows[option.workflowId] || {};
  const workflowStage = normalizedPipelineStages[option.workflowStageId] || {};
  const label = [showWorkflow && workflow.name, showStage && workflowStage.name, option.name]
    .filter(Boolean)
    .join(': ');
  return `${label} ${adaptIsTerminal(option.isTerminal)} ${option.disabledFrom ? `(${translate('Disabled')})` : ''}`;
};

export const highlighted = (highlight, value, defaultValue) => get(highlight, [value, 0], defaultValue);

export const prepareHighlights = (highlight = {}, path = '') =>
  (highlight[path] || []).reduce((prev, cur) => {
    const trimmed = cur.replace(/<mark.*?>(.*?)<\/mark>/gi, '$1');
    return {
      ...prev,
      [trimmed]: cur,
    };
  }, {});

let workLocationPreferenceTypesOptions;
export const getWorkLocationPreferenceOptions = () => {
  if (workLocationPreferenceTypesOptions) {
    return workLocationPreferenceTypesOptions;
  }
  workLocationPreferenceTypesOptions = Object.keys(workLocationPreferenceTypes).map(
    (workLocationPreferenceTypeKey) => ({
      code: workLocationPreferenceTypeKey,
      label: translate(upperFirst(lowerCase(workLocationPreferenceTypeKey))),
    }),
  );
  return getWorkLocationPreferenceOptions();
};
let subscriptionTypesLabels;
export const getSubscriptionTypesLabels = () => {
  if (subscriptionTypesLabels) {
    return subscriptionTypesLabels;
  }
  subscriptionTypesLabels = {
    [subscriptionTypes.legitimateInterest]: translate('Legitimate interest'),
    [subscriptionTypes.consentObtained]: translate('Consent Obtained'),
    [subscriptionTypes.unsubscribeAutomatic]: translate('Unsubscribe: Job notifications & marketing'),
    [subscriptionTypes.unsubscribeEmails]: translate('Unsubscribe: All emails'),
    [subscriptionTypes.unsubscribeCommunications]: translate('Unsubscribe: All communications'),
  };
  return getSubscriptionTypesLabels();
};
let subscriptionTypesOptions;
export const getSubscriptionTypesOptions = () => {
  if (subscriptionTypesOptions) {
    return subscriptionTypesOptions;
  }
  subscriptionTypesOptions = Object.entries(getSubscriptionTypesLabels()).map(([id, label]) => ({
    id,
    label,
  }));
  return getSubscriptionTypesOptions();
};

export const candidatesIfPipelinesCrud = (entitiesType) =>
  entitiesType === entitiesTypes.pipelinesCrud ? entitiesTypes.candidates : entitiesType;

// "values" not "body" since should be able to manipulate paths

export const isFinalFormContext = () => {
  try {
    useForm(); // eslint-disable-line react-hooks/rules-of-hooks
    return true;
  } catch (err) {
    return false;
  }
};

export const toJsIfImmutable = (value) => (value?.toJS ? value.toJS() : value);

export const getClassNames = (...initialClassNames) => {
  let classNames = initialClassNames;
  if (Array.isArray(classNames[0])) {
    classNames = initialClassNames[0];
    logError('getClassNames() should not be used with arrays');
  }
  const filteredClassNames = classNames.filter(Boolean);
  if (filteredClassNames.some((filteredClassName) => filteredClassName && typeof filteredClassName !== 'string')) {
    logError('All getClassNames() arguments must be strings');
  }
  return filteredClassNames.join(' ');
};

export const isAgentIntersect = (accountTypes = []) => !!intersection(agentContactAccountTypes, accountTypes).length;

export const getAccountTypes = ({ accountTypes }) => {
  if (!accountTypes) {
    return wipeStorage();
  }
  return accountTypes;
};

export const isAccessToSelectPortal = (portals = []) => !!(portals.length > 1);

export const getAccessPortals = (accountTypes: AccountType[] = []) =>
  without(uniq(accountTypes.map((item) => accountTypeAccessPortal[item])), undefined);

export const removeScriptTags = (html) => {
  if (typeof html !== 'string') {
    return html;
  }

  let result = html;
  let counter = 0;

  while (removeScriptRegex.test(result) && counter < 10) {
    result = result.replace(removeScriptRegex, '');
    counter++;
  }

  return result;
};

export const notReadyTranslate = (translation, options) => {
  if (options) {
    return translation.replace(/\{\{(.*?)\}\}/g, (match, token) => options[token.trim()]);
  }
  return translation;
};

export const noop = () => undefined;

export const copy = <T>(value: T): T => {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (err) {
    logError('Unable to copy value');
    logError(err);
    return value;
  }
};

const s4 = () =>
  Math.floor((1 + Math.random()) * 0x10000)
    .toString(16)
    .substring(1);

export const guid = () => `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;

export const stringifyIfObject = <T>(obj: T) =>
  typeof obj === 'object' && obj !== null ? JSON.stringify(obj, null, 1) : obj;

export const isNumeric = (value: unknown): value is number =>
  !Number.isNaN(parseFloat(value as string)) && Number.isFinite(parseFloat(value as string));

export const difference = (object: any, base: any, allowedDiff = []) =>
  transform(object, (result, value, key) => {
    if (!allowedDiff.includes(key) && !isEqual(value, base[key])) {
      result[key] =
        value && typeof value === 'object' && typeof base[key] === 'object'
          ? difference(value, base[key], allowedDiff)
          : value;
    }
  });

export const setTitle = (title = null) => {
  const titlePostfix =
    window.location.hostname === hostnames.localhost
      ? ` (${localStorage.tenant ? localStorage.tenant.toUpperCase() : 'DEV'})`
      : '';

  const titleName = 'Eva';
  const extendedTitleName = `${titleName}${titlePostfix}`;

  if (title) {
    const startCasedTitle = startCase(title);
    if (window.translationsReady) {
      return (document.title = `${translate(startCasedTitle)} | ${extendedTitleName}`);
    }
    document.title = `${startCasedTitle} | ${extendedTitleName}`;
    return;
  }

  document.title = extendedTitleName;
};

export const wipeStorage = () => {
  try {
    updateFirstName(JSON.parse(localStorage.user));
  } catch (err) {
    // do nothing;
  }
  delete localStorage.token;
  delete localStorage.user;
  delete window.isAgent;
};

export const pascalCase = (string: string) => startCase(camelCase(string)).replace(/ /g, '');

export const logDebug = (error: string) => {
  if (!localStorageService.debugLog) {
    return;
  }

  // eslint-disable-next-line no-console
  console.log(error);
};

export const logError = (error: string | Error, context?: OrigContextType) => {
  // eslint-disable-next-line no-console
  console.error(error);

  sentry.captureException(error, context);
};

export const logWarning = (error: string | Error, context?: any) => {
  // eslint-disable-next-line no-console
  console.error(error);

  sentry.captureException(error, { ...context, severity: 'warning' });
};

export const logInfo = (error: string | Error, context?: any) => {
  sentry.captureException(error, { ...context, severity: 'info' });
};

export const parseBool = (value: any) => !!value;

export const findEntityTypeByTypeKey = (entitiesType: keyof typeof entitiesTypes) => {
  const entityTypesKeys = Object.keys(entityTypes);
  const entityTypeKey = entityTypesKeys.find((key) => entityTypes[key].entitiesType === entitiesType);

  return entityTypes[entityTypeKey];
};
